'use client';
import { cva, VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { ComponentPropsWithoutRef } from 'react';
import { formatAddress } from '@/shared/lib/formatAddress';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Address } from '@/shared/model/Address';
import { LocalizedString } from '@/shared/model/Language';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icon';
import { Tags } from '@/shared/ui/tags';
import { TagsT } from '@/shared/ui/tags/types';
import './blog-card.scss';

const blogCardVariants = cva('blog-card', {
    variants: {
        variant: {
            default: 'blog-card--default',
            detailed: 'blog-card--detailed',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

type Props = VariantProps<typeof blogCardVariants> &
    Omit<ComponentPropsWithoutRef<typeof Card>, 'title' | 'id'> & {
        id: string;
        imageUrl?: string;
        tags?: TagsT;
        secondTags?: TagsT;
        areSecondTagsVisible?: boolean;
        areTagsVisible?: boolean;
        isImageTagsVisible?: boolean;
        title: LocalizedString;
        description?: LocalizedString;
        venue?: {
            name: string;
            logoUrl?: string;
            address?: Address;
        };
        publishedAt: string;
        readLength: number;
        author: {
            name: string;
        };
    };
export const BlogCard = ({
    id,
    imageUrl,
    tags,
    areTagsVisible = true,
    isImageTagsVisible,
    className,
    title,
    description,
    venue,
    variant,
    secondTags,
    areSecondTagsVisible = true,
    publishedAt,
    readLength,
    author,
    ...cardProps
}: Props) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const localizedTitle = getLocalizedValue(title);
    const localizedDescription = description ? getLocalizedValue(description) : '';
    const formattedDate = new Date(publishedAt).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    });

    return (
        <Card
            className={blogCardVariants({ variant, className })}
            data-testid="blog-card"
            data-id={id}
            shadow
            {...cardProps}
        >
            {imageUrl && (
                <div className="blog-card__image">
                    {areTagsVisible && isImageTagsVisible && <Tags tags={tags} />}
                    <Image
                        src={imageUrl}
                        alt={`blog-${localizedTitle}-image`}
                        fill
                        sizes="19.5rem"
                        data-testid="blog-card-image"
                    />
                </div>
            )}
            <div className="blog-card__content">
                {areTagsVisible && !isImageTagsVisible && variant === 'default' && (
                    <Tags tags={tags} appearance="outline" />
                )}
                {areSecondTagsVisible && variant === 'detailed' && (
                    <Tags tags={secondTags} appearance="outline" />
                )}
                {variant === 'detailed' && (
                    <span className="blog-card__details" data-testid="blog-card-published-at">
                        <Icon name="clock" />
                        {t('BLOG.CARD.DETAILS', { date: formattedDate, readLength })}
                    </span>
                )}
                <span className="blog-card__title" data-testid="blog-card-title">
                    {localizedTitle}
                </span>
                {localizedDescription && (
                    <p className="blog-card__description" data-testid="blog-card-description">
                        {localizedDescription}
                    </p>
                )}
                {venue && variant === 'default' && (
                    <div className="blog-card__venue">
                        {venue.logoUrl && (
                            <div>
                                <Image
                                    src={venue.logoUrl}
                                    alt={`venue-${title}-logo`}
                                    fill
                                    sizes="2rem"
                                    data-testid="blog-card-venue-logo"
                                />
                            </div>
                        )}
                        <span>
                            <span data-testid="blog-card-venue-name">{venue.name}</span>
                            {venue.address && (
                                <span data-testid="blog-card-venue-address">{` - ${formatAddress(venue.address)}`}</span>
                            )}
                        </span>
                    </div>
                )}
                {variant === 'detailed' && (
                    <span className="blog-card__author">
                        <Icon name="user" />
                        {author.name}
                    </span>
                )}
            </div>
        </Card>
    );
};
